import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { List, type RowComponentProps } from 'react-window';
import type { PropertySummary, PropertyStatus, ListPropertiesTierInfo } from '../types/api';
import { PropertyCard } from './PropertyCard';
import { Spinner } from './index';
import { X } from 'lucide-react';
import { EXTENSION_URL } from './landing/landingData';

const MIN_CARD_WIDTH = 340;
const GRID_GAP = 20;
const ROW_HEIGHT = 340; // 320px card + 20px gap
const VIRTUALIZATION_THRESHOLD = 50;

type VirtualRowProps = {
  rows: PropertySummary[][];
  cols: number;
  onClick: (id: string) => void;
  cIds?: Set<string>;
  onToggle?: (id: string) => void;
  locName?: string;
  activeId?: string | null;
};

function VirtualRow({ index, style, rows, cols, onClick, cIds, onToggle, locName, activeId }: RowComponentProps<VirtualRowProps>) {
  const row = rows[index];
  if (!row) return null;
  return (
    <div style={style}>
      <div className="virtual-grid-row">
        {row.map((property) => (
          <div key={property.id} className="virtual-grid-cell">
            <PropertyCard
              property={property}
              onClick={() => onClick(property.id)}
              compareSelected={cIds?.has(property.id)}
              onToggleCompare={onToggle}
              primaryLocationName={locName}
              active={property.id === activeId}
            />
          </div>
        ))}
        {row.length < cols && Array.from({ length: cols - row.length }, (_, i) => (
          <div key={`empty-${i}`} className="virtual-grid-cell" />
        ))}
      </div>
    </div>
  );
}

interface PropertyListProps {
  properties: PropertySummary[];
  loading: boolean;
  error: string | null;
  statusFilter: PropertyStatus | 'all' | 'excluded_rejected';
  onStatusFilterChange: (status: PropertyStatus | 'all' | 'excluded_rejected') => void;
  onPropertyClick: (id: string) => void;
  onRetry?: () => void;
  tierInfo?: ListPropertiesTierInfo | null;
  compareIds?: Set<string>;
  onToggleCompare?: (id: string) => void;
  primaryLocationName?: string;
  activePropertyId?: string | null;
  onAddProperty?: () => void;
}

const STATUS_TABS: { value: PropertyStatus | 'all' | 'excluded_rejected'; labelKey: string }[] = [
  { value: 'all', labelKey: 'status.all' },
  { value: 'new', labelKey: 'status.new' },
  { value: 'interested', labelKey: 'status.interested' },
  { value: 'viewing', labelKey: 'status.viewing' },
  { value: 'applied', labelKey: 'status.applied' },
  { value: 'excluded_rejected', labelKey: 'status.excluded' },
  { value: 'rented', labelKey: 'status.rented' },
];

function matchesSearch(property: PropertySummary, query: string): boolean {
  const lower = query.toLowerCase();

  if (property.address.toLowerCase().includes(lower)) return true;
  if (property.site.toLowerCase().includes(lower)) return true;

  return false;
}

export function PropertyList({
  properties,
  loading,
  error,
  statusFilter,
  onStatusFilterChange,
  onPropertyClick,
  onRetry,
  tierInfo: _tierInfo,
  compareIds,
  onToggleCompare,
  primaryLocationName,
  activePropertyId,
  onAddProperty,
}: PropertyListProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'price-asc' | 'price-desc' | 'rating'>('newest');
  const [hideExcluded, setHideExcluded] = useState(() => {
    try { return localStorage.getItem('movetwo_hide_excluded') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const statusCounts = properties.reduce(
    (acc, p) => {
      acc[p.status] = (acc[p.status] || 0) + 1;
      if (p.status === 'excluded' || p.status === 'rejected') {
        acc['excluded_rejected'] = (acc['excluded_rejected'] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  const toggleHideExcluded = () => {
    setHideExcluded((prev) => {
      const next = !prev;
      try { localStorage.setItem('movetwo_hide_excluded', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  let filteredProperties =
    statusFilter === 'all'
      ? hideExcluded
        ? properties.filter((p) => p.status !== 'excluded' && p.status !== 'rejected')
        : properties
      : statusFilter === 'excluded_rejected'
        ? properties.filter((p) => p.status === 'excluded' || p.status === 'rejected')
        : properties.filter((p) => p.status === statusFilter);

  if (debouncedQuery) {
    filteredProperties = filteredProperties.filter((p) => matchesSearch(p, debouncedQuery));
  }

  filteredProperties = [...filteredProperties].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'price-asc':
        return a.rent - b.rent;
      case 'price-desc':
        return b.rent - a.rent;
      case 'rating': {
        const rA = a.userRating || 0;
        const rB = b.userRating || 0;
        if (rA === 0 && rB === 0) return 0;
        if (rA === 0) return 1;
        if (rB === 0) return -1;
        return rB - rA;
      }
      default:
        return 0;
    }
  });

  const isSearching = debouncedQuery.length > 0;

  // Virtual scroll: measure container width to calculate columns
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columnCount = containerWidth > 0
    ? Math.max(1, Math.floor((containerWidth + GRID_GAP) / (MIN_CARD_WIDTH + GRID_GAP)))
    : 1;

  const shouldVirtualize = filteredProperties.length >= VIRTUALIZATION_THRESHOLD && containerWidth > 0;

  // Group properties into rows
  const rows = useMemo(() => {
    if (!shouldVirtualize) return [];
    const result: PropertySummary[][] = [];
    for (let i = 0; i < filteredProperties.length; i += columnCount) {
      result.push(filteredProperties.slice(i, i + columnCount));
    }
    return result;
  }, [filteredProperties, columnCount, shouldVirtualize]);

  return (
    <div className="property-list" ref={containerRef}>
      <div className="property-search">
        <div className="property-search-input-wrap">
          <input
            type="text"
            className="property-search-input"
            placeholder={t('app.propertyList.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="property-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label={t('app.propertyList.clearSearch')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        {onAddProperty && (
          <button className="add-property-btn" onClick={onAddProperty} aria-label={t('app.propertyList.addProperty')}>{t('app.propertyList.addProperty')}</button>
        )}
      </div>

      {isSearching && !loading && !error && (
        <div className="search-result-count">
          {filteredProperties.length} of {properties.length} properties
        </div>
      )}

      <div className="list-filters-row">
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as typeof statusFilter)}
        >
          {STATUS_TABS.map((tab) => (
            <option key={tab.value} value={tab.value}>
              {t(tab.labelKey)} ({tab.value === 'all' ? properties.length : statusCounts[tab.value] || 0})
            </option>
          ))}
        </select>
        <select
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
        >
          <option value="newest">{t('app.propertyList.sort.newest')}</option>
          <option value="price-asc">{t('app.propertyList.sort.priceLow')}</option>
          <option value="price-desc">{t('app.propertyList.sort.priceHigh')}</option>
          <option value="rating">{t('app.propertyList.sort.rating')}</option>
        </select>
        {statusFilter === 'all' && (
          <label className="hide-excluded-label">
            <input type="checkbox" checked={hideExcluded} onChange={toggleHideExcluded} />
            {t('app.propertyList.hideExcluded')}
          </label>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <Spinner />
          <p>{t('app.propertyList.loading')}</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
          {onRetry && (
            <button className="btn btn-primary btn-sm retry-btn" onClick={onRetry}>
              {t('common.retry')}
            </button>
          )}
        </div>
      )}

      {!loading && !error && filteredProperties.length === 0 && (
        <div className="empty-state">
          {isSearching ? (
            <p>{t('app.propertyList.noResultsSearch')}</p>
          ) : (
            <>
              <p>{t('app.propertyList.noPropertiesSaved')}</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>{t('app.propertyList.browseWithExtension')} <a href={EXTENSION_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>{t('app.propertyList.extensionLink')}</a> {t('app.propertyList.toSaveProperties')}</p>
            </>
          )}
        </div>
      )}

      {!loading && !error && filteredProperties.length > 0 && (
        shouldVirtualize ? (
          <List
            rowComponent={VirtualRow}
            rowCount={rows.length}
            rowHeight={ROW_HEIGHT}
            rowProps={{ rows, cols: columnCount, onClick: onPropertyClick, cIds: compareIds, onToggle: onToggleCompare, locName: primaryLocationName, activeId: activePropertyId }}
            overscanCount={3}
            className="virtual-grid-container"
            style={{ height: window.innerHeight - 180 }}
          />
        ) : (
          <div className="property-grid">
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onClick={() => onPropertyClick(property.id)}
                compareSelected={compareIds?.has(property.id)}
                onToggleCompare={onToggleCompare}
                primaryLocationName={primaryLocationName}
                active={property.id === activePropertyId}
              />
            ))}
          </div>
        )
      )}

    </div>
  );
}
